import en from '../i18n/en.json';
import es from '../i18n/es.json';
import pt from '../i18n/pt.json';
import epxMl from '../moddle/epx-ml.json';
import { DmnReader } from '../io/DmnReader';
import { DmnWriter } from '../io/DmnWriter';
import { DrdEditor } from '../drd/DrdEditor';
import { SHAPE_DEFAULTS } from '../drd/shapes/index';
import { DecisionTableEditor } from '../table/DecisionTableEditor';
import { BoxedExpressionEditor } from '../expression/BoxedExpressionEditor';
import { createExpression, createContextEntry, createInformationItem } from '../expression/shared';
import { PropertiesPanel } from '../properties/PropertiesPanel';
import { Validator } from '../validation/Validator';
import { buildModel } from '../io/model';
import { extractFreeIdents } from '../validation/rules';
import { sanitizeFeelIdent } from '../feel/ident';
import { ensureInputDataForName, ensureInformationRequirement, ensureFormalParameterForBkm } from '../drd/promoteInput';
import { SimulatorDock } from '../layout/SimulatorDock';
import '../layout/dockStyles.css';
import { BUILTIN_THEMES, resolveTheme, applyThemeCssVars } from '../drd/theme';

function _uid() { return Math.random().toString(36).slice(2, 9); }

const I18N = { en, es, pt };

export const VIEWS = {
  DRD: 'drd',
  TABLE: 'table',
  EXPRESSION: 'expression',
};

export class EpxDmn {
  constructor({
    container,
    propertiesContainer = null,
    locale = 'en',
    view = VIEWS.DRD,
    theme = 'default',
    grid = null,
    simulator = {},
  } = {}) {
    if (!container) {
      throw new Error('[epx-dmn] container is required');
    }

    this._containerEl = typeof container === 'string'
      ? document.querySelector(container)
      : container;
    this._propertiesContainerEl = typeof propertiesContainer === 'string'
      ? document.querySelector(propertiesContainer)
      : propertiesContainer;

    if (!I18N[locale]) I18N[locale] = { ...I18N.en };
    this._locale = locale;
    this._i18n = I18N[locale];

    // ── Theme registry ────────────────────────────────────────────────────
    // Pre-seeded with the three built-in themes that mirror epx-flow. Hosts
    // (e.g. sfd-v2) call registerTheme() to add custom palettes — the same
    // theme objects they pass to epxFlow.registerTheme().
    this._themeRegistry = new Map(Object.entries(BUILTIN_THEMES));
    this._currentThemeName = typeof theme === 'string' ? theme : null;
    this._currentTheme = this._resolveThemeInput(theme);
    this._applyThemeToHostElements();

    // ── Grid options forwarded to DrdEditor on mount ──────────────────────
    // Accept boolean shorthand (grid: false) to mirror epx-flow's API so the
    // host (sfd-v2) can keep BPMN and DMN grids in sync with one option shape.
    if (grid === false) this._gridOptions = { visible: false };
    else if (grid === true) this._gridOptions = { visible: true };
    else this._gridOptions = grid || null;

    this._definitions = null;
    this._model = null;
    this._view = view;
    this._activeEditor = null;
    this._eventHandlers = {};
    this._pendingExpressionElementId = null;

    this._reader = new DmnReader();
    this._writer = new DmnWriter();
    // Register the EPX ML moddle extension on both reader and writer so the
    // round-trip preserves <epx:machineLearningDecision …/> children. The
    // extension covers MachineLearningDecision (subclass of dmn:Expression),
    // InputBinding, OutputBinding and Fallback. Registered before any read or
    // write so the moddle factory is built with the descriptor in place.
    this._reader.addModdleExtension('epxml', epxMl);
    this._writer.addModdleExtension('epxml', epxMl);
    this._validator = new Validator();
    this._propertiesPanel = null;
    if (this._propertiesContainerEl) {
      this._propertiesPanel = new PropertiesPanel(this._propertiesContainerEl, {
        moddle: this._reader.getModdle(),
        onChange: (p) => {
          this._emit('element.changed', p);
          this._rerenderActive();
          this._revalidate();
        },
        onCreateMissingInputs: (decisionId) => this.createMissingBkmInputs(decisionId),
        onSyncBkmBindings: (bkmId) => this.syncBkmBindings(bkmId),
        onSetInputType: (name, typeRef) => this._setInputTypeByName(name, typeRef),
        onPromoteOrphan: (name) => this._promoteOrphanByName(name),
      });
    }

    this._containerEl.classList.add('epx-dmn');

    // Build the horizontal layout: editor area on the left, simulator dock on
    // the right. The active editor mounts into _editorAreaEl, never into the
    // raw container.
    this._editorAreaEl = document.createElement('div');
    this._editorAreaEl.className = 'epx-editor-area';
    this._containerEl.appendChild(this._editorAreaEl);

    this._simulatorOptions = {
      defaultRatio: typeof simulator.defaultRatio === 'number' ? simulator.defaultRatio : 0.38,
      storageKey: simulator.storageKey || null,
    };
    this._simulatorDock = new SimulatorDock({
      rootEl: this._containerEl,
      editorAreaEl: this._editorAreaEl,
      epxDmn: this,
      storageKey: this._simulatorOptions.storageKey,
      defaultRatio: this._simulatorOptions.defaultRatio,
      onRevealCell: (payload) => this._handleSimulatorReveal(payload),
      onToggle: ({ open }) => this._emit('simulator:toggle', { open }),
      onResize: () => {
        const ed = this._activeEditor;
        if (ed && typeof ed.resize === 'function') {
          try { ed.resize(); } catch { /* noop */ }
        }
      },
    });

    // Apply CSS theme variables to the host containers BEFORE mounting any
    // editor — table/expression styles read `var(--epx-dmn-panel-*)` which
    // only resolve when the variables are set on an ancestor.
    this._applyThemeToHostElements();

    this._mountView();
    this._wirePanelToEvents();
  }

  _wirePanelToEvents() {
    if (!this._propertiesPanel) return;
    this.on('element.click', ({ element }) => {
      if (!element) return this._propertiesPanel.hide();
      const raw = this._resolveModdleElement(element);
      this._propertiesPanel.show(raw);
    });
  }

  _resolveModdleElement(clicked) {
    if (!clicked) return null;
    if (clicked.$type) return clicked;
    // LogicFlow gives us {id, type, properties} — look up by id in the model
    const id = clicked.id;
    if (!id || !this._model) return null;
    const drg = (this._model.drgElements || []).find((el) => el.id === id);
    if (drg) return drg.raw;
    for (const el of (this._model.drgElements || [])) {
      const req = (el.requirements || []).find((r) => r.id === id);
      if (req) return req.raw;
    }
    return null;
  }

  _rerenderActive() {
    if (this._view === VIEWS.DRD && this._activeEditor && this._model) {
      this._activeEditor.render(this._model);
    } else if (
      this._view === VIEWS.EXPRESSION
      && this._activeEditor
      && typeof this._activeEditor.refresh === 'function'
    ) {
      if (typeof this._activeEditor.setDefinitions === 'function') {
        this._activeEditor.setDefinitions(this._definitions);
      }
      this._activeEditor.refresh();
    }
    if (this._inlineEditor && typeof this._inlineEditor.refresh === 'function') {
      if (typeof this._inlineEditor.setDefinitions === 'function') {
        this._inlineEditor.setDefinitions(this._definitions);
      }
      this._inlineEditor.refresh();
    }
  }

  _revalidate() {
    if (!this._definitions) return;
    const issues = this._validator.validate(this._definitions);
    this._emit('validate.done', { issues });
    if (this._activeEditor && typeof this._activeEditor.setIssues === 'function') {
      this._activeEditor.setIssues(issues);
    }
    if (this._inlineEditor && typeof this._inlineEditor.setIssues === 'function') {
      this._inlineEditor.setIssues(issues);
    }
  }

  getValidator() { return this._validator; }

  validate() {
    if (!this._definitions) return [];
    const issues = this._validator.validate(this._definitions);
    this._emit('validate.done', { issues });
    return issues;
  }

  addModdleExtension(name, descriptor) {
    this._reader.addModdleExtension(name, descriptor);
    this._writer.addModdleExtension(name, descriptor);
  }

  _mountView() {
    this._disposeActiveEditor();
    this._editorAreaEl.innerHTML = '';

    if (this._view === VIEWS.DRD) {
      this._activeEditor = new DrdEditor(this._editorAreaEl, {
        theme: this._currentTheme,
        grid: this._gridOptions || undefined,
        eventHandlers: {
          'element.click':   [(p) => this._emit('element.click', p)],
          'element.changed': [(p) => this._emit('element.changed', p)],
          'element.added':   [(p) => this._bridgeOnElementAdded(p)],
          'element.removed': [(p) => this._bridgeOnElementRemoved(p)],
          'element.moved':   [(p) => this._bridgeOnElementMoved(p)],
          'element.renamed': [(p) => this._bridgeOnElementRenamed(p)],
          'edge.added':      [(p) => this._bridgeOnEdgeAdded(p)],
          'edge.removed':    [(p) => this._bridgeOnEdgeRemoved(p)],
          'layout.applied':  [(p) => this._bridgeOnLayoutApplied(p)],
          'node.openTable':  [(p) => this._openTableForDecision(p?.element?.id)],
          'node.inlineEdit': [(p) => this._openInlineTableEditor(p)],
          'selection.changed': [(p) => this._handleSelectionChanged(p)],
        },
      });
      if (this._model) this._activeEditor.render(this._model);
    } else if (this._view === VIEWS.TABLE) {
      this._activeEditor = new DecisionTableEditor(this._editorAreaEl, {
        moddle: this._reader.getModdle(),
        onChange: (p) => {
          this._emit('decisionTable.changed', p);
          this._revalidate();
        },
        onPromoteInput: (p) => this._promoteInputToDrd(p),
        onSwitchLogicType: (newType) => {
          const d = this._activeEditor && this._activeEditor.getDecision
            ? this._activeEditor.getDecision() : null;
          if (d && d.id) this._switchExpandedDecisionLogic(d.id, newType);
        },
      });
      if (this._definitions) this._activeEditor.setDefinitions(this._definitions);
      const preferred = this._pendingTableDecisionId;
      this._pendingTableDecisionId = null;
      const pick = this._findDecisionWithTable(preferred);
      if (pick) this._activeEditor.render(pick);
      this._revalidate();
    } else if (this._view === VIEWS.EXPRESSION) {
      this._activeEditor = new BoxedExpressionEditor(this._editorAreaEl, {
        moddle: this._reader.getModdle(),
        onChange: (p) => this._emit('expression.changed', p),
        onSwitchLogicType: (newType) => {
          const d = this._activeEditor && this._activeEditor.getDecision
            ? this._activeEditor.getDecision() : null;
          if (d && d.id) this._switchExpandedDecisionLogic(d.id, newType);
        },
        onPromoteOrphan: (name, target) => this._promoteOrphanByName(name, target),
        onOpened: (p) => this._emit('expression.editor.opened', p),
      });
      if (this._definitions) this._activeEditor.setDefinitions(this._definitions);
      const preferredExpr = this._pendingExpressionElementId;
      this._pendingExpressionElementId = null;
      const pick = preferredExpr
        ? (this._findExpressionForElement(preferredExpr) || this._findExpressionCandidate())
        : this._findExpressionCandidate();
      if (pick) this._activeEditor.render(pick);
    } else {
      this._renderStub();
    }
  }

  _findDecisionWithTable(elementId) {
    if (!this._model) return null;
    const drg = this._model.drgElements || [];

    const bkmTableOf = (bkm) => {
      const funcDef = bkm.encapsulatedLogic && bkm.encapsulatedLogic.raw;
      if (!funcDef) return null;
      return (funcDef.body && funcDef.body.$type === 'dmn:DecisionTable') ? funcDef.body : null;
    };

    if (elementId) {
      const d = drg.find((el) => el.id === elementId && el.kind === 'decision'
        && el.decisionLogic && el.decisionLogic.type === 'dmn:DecisionTable');
      if (d) return { decision: d.raw, decisionTable: d.decisionLogic.raw };

      const b = drg.find((el) => el.id === elementId && el.kind === 'businessKnowledgeModel');
      if (b) {
        const table = bkmTableOf(b);
        if (table) return { decision: b.raw, decisionTable: table };
      }
      // The requested element exists but has no decision table (e.g. uses invocation or
      // literal expression). Do not fall back to a different element.
      return null;
    }

    const d = drg.find((el) => el.kind === 'decision'
      && el.decisionLogic && el.decisionLogic.type === 'dmn:DecisionTable');
    if (d) return { decision: d.raw, decisionTable: d.decisionLogic.raw };

    const b = drg.find((el) => el.kind === 'businessKnowledgeModel');
    if (b) {
      const table = bkmTableOf(b);
      if (table) return { decision: b.raw, decisionTable: table };
    }

    return null;
  }

  _openTableForDecision(decisionId) {
    if (decisionId) {
      if (this._findDecisionWithTable(decisionId)) {
        this._pendingTableDecisionId = decisionId;
        this.setView(VIEWS.TABLE);
      } else if (this._findExpressionForElement(decisionId)) {
        this._pendingExpressionElementId = decisionId;
        this.setView(VIEWS.EXPRESSION);
      }
      return;
    }
    this._pendingTableDecisionId = null;
    this.setView(VIEWS.TABLE);
  }

  _promoteInputToDrd({ inputClause, decision }) {
    if (!inputClause || !decision) return;
    const text = inputClause.inputExpression && inputClause.inputExpression.text;
    const idents = extractFreeIdents(text || '');
    const defs = this._ensureDefinitions();
    const connected = new Set();
    const reqs = [
      ...(decision.informationRequirement || []),
      ...(decision.knowledgeRequirement || []),
    ];
    reqs.forEach((req) => {
      ['requiredDecision', 'requiredInput', 'requiredKnowledge'].forEach((ref) => {
        if (!req[ref]) return;
        const href = req[ref].href;
        const targetId = (href || '').split('#').pop();
        const target = (defs.drgElement || []).find((e) => e.id === targetId);
        if (!target) return;
        const varName = target.variable && target.variable.name;
        if (varName && varName.trim()) connected.add(varName.trim());
        if (target.name && target.name.trim()) {
          connected.add(target.name.trim());
          const ident = sanitizeFeelIdent(target.name);
          if (ident) connected.add(ident);
        }
      });
    });
    const missing = idents.find((n) => !connected.has(n));
    if (!missing) return;

    const moddle = this._reader.getModdle();
    const typeHint = (inputClause.inputExpression && inputClause.inputExpression.typeRef) || 'string';
    const { inputData, created } = ensureInputDataForName({
      moddle,
      definitions: defs,
      name: missing,
      typeRefHint: typeHint,
    });

    if (created) {
      const diagram = this._ensureDmnDiagram();
      const decShape = (diagram.diagramElements || []).find((de) => {
        if (de.$type !== 'dmndi:DMNShape') return false;
        const ref = de.dmnElementRef;
        if (!ref) return false;
        const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        return rid === decision.id;
      });
      const dx = decShape && decShape.bounds ? decShape.bounds.x : 200;
      const dy = decShape && decShape.bounds ? decShape.bounds.y : 200;
      const sz = SHAPE_DEFAULTS['dmn:InputData'];
      const bounds = moddle.create('dc:Bounds', {
        x: dx - sz.width - 40,
        y: dy + 120,
        width: sz.width,
        height: sz.height,
      });
      const shape = moddle.create('dmndi:DMNShape', {
        id: 'DMNShape_' + _uid(),
        dmnElementRef: inputData,
        bounds,
      });
      bounds.$parent = shape;
      shape.$parent = diagram;
      if (!diagram.diagramElements) diagram.diagramElements = [];
      diagram.diagramElements.push(shape);
    }

    const req = ensureInformationRequirement({ moddle, decision, inputData });
    if (!req) {
      // El requirement ya existía: nada nuevo que dibujar para el edge.
      this._rebuildModel();
      this._revalidate();
      this._rerenderActive();
      return;
    }

    const diagram = this._ensureDmnDiagram();
    const findShape = (id) => (diagram.diagramElements || []).find((de) => {
      if (de.$type !== 'dmndi:DMNShape') return false;
      const ref = de.dmnElementRef;
      if (!ref) return false;
      const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
      return rid === id;
    });
    const inputShape = findShape(inputData.id);
    const decisionShape = findShape(decision.id);
    const ib = (inputShape && inputShape.bounds) || null;
    const db = (decisionShape && decisionShape.bounds) || null;
    const startPoint = ib
      ? { x: (ib.x || 0) + (ib.width || 0), y: (ib.y || 0) + (ib.height || 0) / 2 }
      : { x: 0, y: 0 };
    const endPoint = db
      ? { x: db.x || 0, y: (db.y || 0) + (db.height || 0) / 2 }
      : { x: startPoint.x + 200, y: startPoint.y };
    const wp = [
      moddle.create('dc:Point', { x: startPoint.x, y: startPoint.y }),
      moddle.create('dc:Point', { x: endPoint.x, y: endPoint.y }),
    ];
    const dmnEdge = moddle.create('dmndi:DMNEdge', {
      id: 'DMNEdge_' + _uid(),
      dmnElementRef: req,
      waypoint: wp,
    });
    wp.forEach((p) => (p.$parent = dmnEdge));
    dmnEdge.$parent = diagram;
    if (!diagram.diagramElements) diagram.diagramElements = [];
    diagram.diagramElements.push(dmnEdge);

    this._rebuildModel();
    this._revalidate();
    this._rerenderActive();
    this._emit('element.changed', { element: inputData, change: 'promoted' });
  }

  // Edita el typeRef del input identificado por nombre. Prefiere el InputData
  // del DRD (fuente de verdad). Si no existe, hace write-through al
  // inputExpression.typeRef de las columnas que lo referencian.
  _setInputTypeByName(name, typeRef) {
    const defs = this._ensureDefinitions();
    const moddle = this._reader.getModdle();
    const inputData = (defs.drgElement || []).find(
      (e) => e.$type === 'dmn:InputData' && e.name === name
    );
    if (inputData) {
      if (!inputData.variable) {
        inputData.variable = moddle.create('dmn:InformationItem', {
          id: inputData.id + '_var',
          name: sanitizeFeelIdent(inputData.name) || inputData.name,
          typeRef,
        });
        inputData.variable.$parent = inputData;
      } else {
        inputData.variable.typeRef = typeRef;
      }
    } else {
      (defs.drgElement || []).forEach((drg) => {
        if (drg.$type !== 'dmn:Decision') return;
        const dt = drg.decisionLogic;
        if (!dt || dt.$type !== 'dmn:DecisionTable') return;
        (dt.input || []).forEach((inp) => {
          const t = inp.inputExpression && inp.inputExpression.text;
          if (t && t.trim() === name) inp.inputExpression.typeRef = typeRef;
        });
      });
    }
    this._rebuildModel();
    this._revalidate();
    this._rerenderActive();
  }

  _promoteOrphanByName(name, explicitTarget) {
    const target = explicitTarget
      || (this._propertiesPanel ? this._propertiesPanel.getCurrent() : null);
    if (!target) return;
    const moddle = this._reader.getModdle();

    if (target.$type === 'dmn:Decision') {
      const synthExpr = moddle.create('dmn:LiteralExpression', {
        id: 'SynthExpr_' + _uid(),
        text: name,
        typeRef: 'string',
      });
      const synth = moddle.create('dmn:InputClause', {
        id: 'Synth_' + _uid(),
        inputExpression: synthExpr,
      });
      synthExpr.$parent = synth;
      this._promoteInputToDrd({ inputClause: synth, decision: target });
      return;
    }

    if (target.$type === 'dmn:BusinessKnowledgeModel') {
      const hint = this._typeHintForBkmOrphan(target, name);
      const { parameter, created } = ensureFormalParameterForBkm({
        moddle,
        bkm: target,
        name,
        typeRefHint: hint,
      });
      if (!created) return;
      this._rebuildModel();
      this._revalidate();
      this._rerenderActive();
      this._emit('element.changed', { element: parameter, change: 'promoted' });
    }
  }

  _typeHintForBkmOrphan(bkm, name) {
    const logic = bkm.encapsulatedLogic;
    if (!logic) return null;
    const inner = logic.$type === 'dmn:FunctionDefinition' ? logic.body : logic;
    if (!inner) return null;
    if (inner.$type === 'dmn:DecisionTable') {
      const inp = (inner.input || []).find(
        (i) => i.inputExpression && (i.inputExpression.text || '').trim() === name
      );
      return (inp && inp.inputExpression && inp.inputExpression.typeRef) || null;
    }
    return null;
  }

  _openInlineTableEditor({ element } = {}) {
    if (!element || (element.type !== 'dmn:Decision' && element.type !== 'dmn:BusinessKnowledgeModel')) return;

    const tablePick = this._findDecisionWithTable(element.id);
    if (tablePick) {
      this._openInlineDecisionTableEditor(tablePick, element);
      return;
    }
    const exprPick = this._findExpressionForElement(element.id);
    if (exprPick) {
      this._openInlineExpressionEditor(exprPick, element);
      return;
    }

    // Decision without any logic — seed it with a default DecisionTable
    // so the overlay always has something to render. The user can switch
    // the logic type from the header chip.
    if (element.type === 'dmn:Decision') {
      const drg = (this._definitions && this._definitions.drgElement) || [];
      const decisionRaw = drg.find((e) => e.id === element.id && e.$type === 'dmn:Decision');
      if (decisionRaw) {
        const table = this._createDefaultDecisionLogic('dmn:DecisionTable');
        table.$parent = decisionRaw;
        decisionRaw.decisionLogic = table;
        this._rebuildModel();
        this._revalidate();
        const fresh = this._findDecisionWithTable(element.id);
        if (fresh) this._openInlineDecisionTableEditor(fresh, element);
      }
    }
  }

  _buildInlineOverlay(titleText, options = {}) {
    // Back-compat: a lone function positional arg is treated as onOpenFull.
    if (typeof options === 'function') options = { onOpenFull: options };
    const { onOpenFull, logicSwitcher, variant } = options;

    this._ensureInlineOverlayStyles();
    const overlay = document.createElement('div');
    overlay.className = 'epx-dmn-inline-overlay';
    if (this._currentTheme) {
      applyThemeCssVars(overlay, this._currentTheme);
    }

    const card = document.createElement('div');
    card.className = 'epx-dmn-inline-card';
    if (variant === 'table') card.classList.add('epx-dmn-inline-card--table');
    else if (variant === 'expression') card.classList.add('epx-dmn-inline-card--expression');

    const head = document.createElement('div');
    head.className = 'epx-dmn-inline-head';
    const title = document.createElement('span');
    title.className = 'epx-dmn-inline-title';
    title.textContent = titleText;
    head.appendChild(title);

    if (logicSwitcher && logicSwitcher.onChange) {
      head.appendChild(this._buildLogicSwitcherChip(logicSwitcher));
    }

    if (onOpenFull) {
      const btnFull = document.createElement('button');
      btnFull.type = 'button';
      btnFull.className = 'epx-dmn-inline-btn epx-dmn-inline-btn--icon';
      btnFull.title = 'Pantalla completa';
      btnFull.setAttribute('aria-label', 'Pantalla completa');
      btnFull.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">'
        + '<path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
        + '</svg>';
      btnFull.addEventListener('click', onOpenFull);
      head.appendChild(btnFull);
    }

    const btnClose = document.createElement('button');
    btnClose.type = 'button';
    btnClose.className = 'epx-dmn-inline-btn epx-dmn-inline-btn--icon epx-dmn-inline-btn--close';
    btnClose.setAttribute('aria-label', 'Cerrar');
    btnClose.title = 'Cerrar';
    btnClose.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">'
      + '<path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>'
      + '</svg>';
    btnClose.addEventListener('click', () => this._closeInlineEditor());
    head.appendChild(btnClose);

    const body = document.createElement('div');
    body.className = 'epx-dmn-inline-body';

    card.appendChild(head);
    card.appendChild(body);
    overlay.appendChild(card);

    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) this._closeInlineEditor();
    });
    this._inlineKeyHandler = (e) => { if (e.key === 'Escape') this._closeInlineEditor(); };
    document.addEventListener('keydown', this._inlineKeyHandler);

    document.body.appendChild(overlay);
    return { overlay, body };
  }

  _buildLogicSwitcherChip({ currentType, onChange }) {
    const LOGIC_TYPES = [
      { type: 'dmn:DecisionTable',     label: 'Decision Table' },
      { type: 'dmn:LiteralExpression', label: 'Literal Expression' },
      { type: 'dmn:Context',           label: 'Context' },
      { type: 'dmn:List',              label: 'List' },
      { type: 'dmn:Invocation',        label: 'Invocation' },
      { type: 'dmn:Relation',          label: 'Relation' },
      { type: 'dmn:FunctionDefinition',label: 'Function Definition' },
    ];
    const labelFor = (t) => (LOGIC_TYPES.find((x) => x.type === t) || {}).label
      || (t ? t.replace(/^dmn:/, '') : '—');

    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'epx-dmn-inline-logic-chip';
    chip.title = 'Cambiar el tipo de lógica de la decisión';
    chip.textContent = labelFor(currentType) + '  ▾';
    chip.addEventListener('click', (evt) => {
      evt.stopPropagation();
      this._openLogicSwitcherMenu(chip, currentType, LOGIC_TYPES, onChange);
    });
    return chip;
  }

  _openLogicSwitcherMenu(anchor, currentType, options, onPick) {
    document.querySelectorAll('.epx-dmn-inline-logic-menu').forEach((m) => m.remove());
    const menu = document.createElement('div');
    menu.className = 'epx-dmn-inline-logic-menu';
    const rect = anchor.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 4) + 'px';
    options.forEach((opt) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'epx-dmn-inline-logic-item';
      if (opt.type === currentType) b.classList.add('is-active');
      b.textContent = opt.label;
      b.addEventListener('click', () => {
        menu.remove();
        if (opt.type !== currentType) onPick(opt.type);
      });
      menu.appendChild(b);
    });
    document.body.appendChild(menu);
    const close = (e) => {
      if (!menu.contains(e.target) && e.target !== anchor) {
        menu.remove();
        document.removeEventListener('mousedown', close);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }

  _createDefaultDecisionLogic(type) {
    const moddle = this._reader.getModdle();
    if (type === 'dmn:DecisionTable') {
      const inputExpr = moddle.create('dmn:LiteralExpression', { id: 'InputExpr_' + _uid(), text: '' });
      const input = moddle.create('dmn:InputClause', { id: 'Input_' + _uid(), inputExpression: inputExpr, label: 'Input' });
      inputExpr.$parent = input;
      const output = moddle.create('dmn:OutputClause', { id: 'Output_' + _uid(), label: 'Output', typeRef: 'string' });
      const table = moddle.create('dmn:DecisionTable', {
        id: 'DecisionTable_' + _uid(),
        hitPolicy: 'UNIQUE',
        input: [input],
        output: [output],
        rule: [],
      });
      input.$parent = table;
      output.$parent = table;
      return table;
    }
    if (type === 'dmn:LiteralExpression') {
      return moddle.create('dmn:LiteralExpression', { id: 'LitExpr_' + _uid(), text: '' });
    }
    if (type === 'dmn:Context') {
      return moddle.create('dmn:Context', { id: 'Context_' + _uid(), contextEntry: [] });
    }
    if (type === 'dmn:List') {
      return moddle.create('dmn:List', { id: 'List_' + _uid(), elements: [] });
    }
    if (type === 'dmn:Invocation') {
      const called = moddle.create('dmn:LiteralExpression', { id: 'LitExpr_' + _uid(), text: '' });
      const inv = moddle.create('dmn:Invocation', {
        id: 'Invocation_' + _uid(),
        calledFunction: called,
        binding: [],
      });
      called.$parent = inv;
      return inv;
    }
    if (type === 'dmn:Relation') {
      return moddle.create('dmn:Relation', { id: 'Relation_' + _uid(), column: [], row: [] });
    }
    if (type === 'dmn:FunctionDefinition') {
      const body = moddle.create('dmn:LiteralExpression', { id: 'LitExpr_' + _uid(), text: '' });
      const fd = moddle.create('dmn:FunctionDefinition', {
        id: 'FunctionDef_' + _uid(),
        kind: 'FEEL',
        formalParameter: [],
        body,
      });
      body.$parent = fd;
      return fd;
    }
    return null;
  }

  _switchDecisionLogic(element, newType) {
    if (!element || element.type !== 'dmn:Decision') return;
    const drg = (this._definitions && this._definitions.drgElement) || [];
    const decisionRaw = drg.find((e) => e.id === element.id && e.$type === 'dmn:Decision');
    if (!decisionRaw) return;

    const fresh = this._createDefaultDecisionLogic(newType);
    if (!fresh) return;
    fresh.$parent = decisionRaw;
    decisionRaw.decisionLogic = fresh;

    this._rebuildModel();
    this._rerenderActive();
    this._emit('element.changed', { element, change: 'logic-type' });
    this._revalidate();

    // Re-open the overlay on the new logic type.
    this._closeInlineEditor();
    this._openInlineTableEditor({ element });
  }

  // Swap the Decision's logic type while the user is in an expanded view.
  // If the new type is a DecisionTable we flip to VIEWS.TABLE; otherwise we
  // flip to (or stay in) VIEWS.EXPRESSION. The target decision is re-picked
  // on mount via the pending-id fields so the new editor lands on the same
  // element that was being edited.
  _switchExpandedDecisionLogic(decisionId, newType) {
    if (!decisionId || !newType) return;
    const drg = (this._definitions && this._definitions.drgElement) || [];
    const decisionRaw = drg.find((e) => e.id === decisionId && e.$type === 'dmn:Decision');
    if (!decisionRaw) return;

    const fresh = this._createDefaultDecisionLogic(newType);
    if (!fresh) return;
    fresh.$parent = decisionRaw;
    decisionRaw.decisionLogic = fresh;

    this._rebuildModel();
    this._emit('element.changed', { element: { id: decisionId, type: 'dmn:Decision' }, change: 'logic-type' });
    this._revalidate();

    if (newType === 'dmn:DecisionTable') {
      this._pendingTableDecisionId = decisionId;
      this.setView(VIEWS.TABLE);
    } else {
      this._pendingExpressionElementId = decisionId;
      this.setView(VIEWS.EXPRESSION);
    }
  }

  _openInlineDecisionTableEditor(pick, element) {
    this._closeInlineEditor();
    const overlayOpts = {
      onOpenFull: () => { this._closeInlineEditor(); this._openTableForDecision(element.id); },
      variant: 'table',
    };
    if (element.type === 'dmn:Decision') {
      overlayOpts.logicSwitcher = {
        currentType: 'dmn:DecisionTable',
        onChange: (newType) => this._switchDecisionLogic(element, newType),
      };
    }
    const { overlay, body } = this._buildInlineOverlay(
      pick.decision.name || element.id,
      overlayOpts,
    );

    const editor = new DecisionTableEditor(body, {
      moddle: this._reader.getModdle(),
      onChange: (p) => {
        this._emit('decisionTable.changed', p);
        this._revalidate();
      },
      onPromoteInput: (p) => this._promoteInputToDrd(p),
    });
    if (this._definitions) editor.setDefinitions(this._definitions);
    editor.render(pick);

    this._inlineEditor = editor;
    this._inlineOverlay = overlay;
    this._revalidate();
  }

  _openInlineExpressionEditor(pick, element) {
    this._closeInlineEditor();
    const displayName = (pick.decision || pick.bkm)?.name || element?.id || 'Expresión';
    const overlayOpts = {
      onOpenFull: () => { this._closeInlineEditor(); this._openTableForDecision(element.id); },
      variant: 'expression',
    };
    if (element && element.type === 'dmn:Decision' && pick.expression) {
      overlayOpts.logicSwitcher = {
        currentType: pick.expression.$type,
        onChange: (newType) => this._switchDecisionLogic(element, newType),
      };
    }
    const { overlay, body } = this._buildInlineOverlay(
      displayName,
      overlayOpts,
    );

    const editor = new BoxedExpressionEditor(body, {
      moddle: this._reader.getModdle(),
      onChange: (p) => {
        this._emit('expression.changed', p);
        this._revalidate();
      },
      onPromoteOrphan: (name, target) => this._promoteOrphanByName(name, target),
      onOpened: (p) => this._emit('expression.editor.opened', p),
    });
    if (this._definitions) editor.setDefinitions(this._definitions);
    editor.render(pick);

    this._inlineEditor = editor;
    this._inlineOverlay = overlay;
    this._revalidate();
  }

  _closeInlineEditor() {
    if (this._inlineEditor && typeof this._inlineEditor.destroy === 'function') {
      try { this._inlineEditor.destroy(); } catch { /* ignore */ }
    }
    this._inlineEditor = null;
    if (this._inlineOverlay && this._inlineOverlay.parentNode) {
      this._inlineOverlay.parentNode.removeChild(this._inlineOverlay);
    }
    this._inlineOverlay = null;
    if (this._inlineKeyHandler) {
      document.removeEventListener('keydown', this._inlineKeyHandler);
      this._inlineKeyHandler = null;
    }
  }

  _ensureInlineOverlayStyles() {
    if (document.getElementById('epx-dmn-inline-overlay-styles')) return;
    const style = document.createElement('style');
    style.id = 'epx-dmn-inline-overlay-styles';
    style.textContent = `
      .epx-dmn-inline-overlay {
        position: fixed;
        inset: 0;
        background: var(--epx-dmn-overlay-scrim);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .epx-dmn-inline-card {
        background: var(--epx-dmn-panel-bg);
        border-radius: 10px;
        box-shadow: 0 24px 48px var(--epx-dmn-overlay-shadow);
        width: min(920px, 94vw);
        max-height: 82vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .epx-dmn-inline-card--expression,
      .epx-dmn-inline-card--table {
        width: min(1360px, 97vw);
        height: 80vh;
        max-height: 80vh;
      }
      .epx-dmn-inline-head {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        border-bottom: 1px solid var(--epx-dmn-panel-border);
        background: var(--epx-dmn-panel-hover);
      }
      .epx-dmn-inline-title {
        flex: 1;
        font-weight: 600;
        color: var(--epx-dmn-panel-text);
        font-size: 14px;
      }
      .epx-dmn-inline-btn {
        border: 1px solid var(--epx-dmn-input-border);
        background: var(--epx-dmn-panel-bg);
        color: var(--epx-dmn-panel-text-muted);
        padding: 4px 10px;
        border-radius: 5px;
        font-size: 12px;
        cursor: pointer;
      }
      .epx-dmn-inline-btn:hover { background: var(--epx-dmn-panel-hover); }
      .epx-dmn-inline-btn--icon {
        padding: 0;
        width: 28px;
        height: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .epx-dmn-inline-btn--icon svg { display: block; }
      .epx-dmn-inline-btn--close { color: var(--epx-dmn-panel-text-muted); }
      .epx-dmn-inline-body {
        flex: 1;
        overflow: auto;
        padding: 0;
        min-height: 200px;
      }
      .epx-dmn-inline-logic-chip {
        border: 1px solid var(--epx-dmn-input-border);
        background: var(--epx-dmn-logic-chip-bg);
        color: var(--epx-dmn-logic-chip-text);
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11.5px;
        font-weight: 600;
        cursor: pointer;
        letter-spacing: 0.2px;
      }
      .epx-dmn-inline-logic-chip:hover { background: var(--epx-dmn-logic-chip-hover); }
      .epx-dmn-inline-logic-menu {
        position: fixed;
        z-index: 10001;
        background: var(--epx-dmn-panel-bg);
        border: 1px solid var(--epx-dmn-input-border);
        border-radius: 8px;
        box-shadow: 0 10px 24px var(--epx-dmn-overlay-menu-shadow);
        padding: 4px;
        min-width: 180px;
      }
      .epx-dmn-inline-logic-item {
        display: block;
        width: 100%;
        text-align: left;
        border: 0;
        background: transparent;
        padding: 6px 10px;
        font-size: 12.5px;
        border-radius: 5px;
        cursor: pointer;
        color: var(--epx-dmn-panel-text);
      }
      .epx-dmn-inline-logic-item:hover { background: var(--epx-dmn-panel-hover); }
      .epx-dmn-inline-logic-item.is-active {
        background: var(--epx-dmn-logic-chip-hover);
        color: var(--epx-dmn-logic-chip-text);
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);
  }

  _findExpressionCandidate() {
    if (!this._model) return null;
    // Prefer a Decision with non-DecisionTable logic, then BKM, then any Decision.
    const drg = this._model.drgElements || [];
    const nonTable = drg.find(
      (el) => el.kind === 'decision' && el.decisionLogic && el.decisionLogic.type !== 'dmn:DecisionTable'
    );
    if (nonTable) return { decision: nonTable.raw, expression: nonTable.decisionLogic.raw };
    const bkm = drg.find((el) => el.kind === 'businessKnowledgeModel' && el.encapsulatedLogic);
    if (bkm) return { bkm: bkm.raw, expression: bkm.encapsulatedLogic.raw };
    const anyDec = drg.find((el) => el.kind === 'decision' && el.decisionLogic);
    if (anyDec) return { decision: anyDec.raw, expression: anyDec.decisionLogic.raw };
    return null;
  }

  _findExpressionForElement(elementId) {
    if (!this._model || !elementId) return null;
    const drg = this._model.drgElements || [];

    const d = drg.find((el) => el.id === elementId && el.kind === 'decision'
      && el.decisionLogic && el.decisionLogic.type !== 'dmn:DecisionTable');
    if (d) return { decision: d.raw, expression: d.decisionLogic.raw };

    const b = drg.find((el) => el.id === elementId && el.kind === 'businessKnowledgeModel'
      && el.encapsulatedLogic);
    if (b) return { bkm: b.raw, expression: b.encapsulatedLogic.raw };

    return null;
  }

  _disposeActiveEditor() {
    if (this._activeEditor && typeof this._activeEditor.destroy === 'function') {
      this._activeEditor.destroy();
    }
    this._activeEditor = null;
  }

  _renderStub() {
    const label = this._view;
    this._editorAreaEl.innerHTML = `
      <div class="epx-dmn-placeholder" style="
        display:flex;align-items:center;justify-content:center;
        width:100%;height:100%;color:#888;font-family:sans-serif;font-size:13px;
        background:repeating-linear-gradient(45deg,#fafafa,#fafafa 10px,#f2f2f2 10px,#f2f2f2 20px);
      ">
        <div style="text-align:center">
          <div style="font-size:18px;font-weight:600;margin-bottom:6px;color:#555;">EPX DMN · ${label}</div>
          <div>editor aún no implementado — llega en fases 3+</div>
        </div>
      </div>
    `;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  async newDecision(name = 'Decision') {
    const uniq = 'Decision_' + Math.random().toString(36).slice(2, 10);
    const xmlName = String(name)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<dmn:definitions xmlns:dmn="https://www.omg.org/spec/DMN/20191111/MODEL/"
                 xmlns:dmndi="https://www.omg.org/spec/DMN/20191111/DMNDI/"
                 xmlns:dc="http://www.omg.org/spec/DMN/20180521/DC/"
                 id="Definitions_${uniq}"
                 name="${xmlName}"
                 namespace="https://epx.com/dmn/${uniq}">
  <dmn:decision id="${uniq}" name="${xmlName}">
    <dmn:decisionTable id="DecisionTable_${uniq}" hitPolicy="UNIQUE">
      <dmn:input id="Input_${uniq}_1" label="Input">
        <dmn:inputExpression id="InputExpr_${uniq}_1" typeRef="string">
          <dmn:text></dmn:text>
        </dmn:inputExpression>
      </dmn:input>
      <dmn:output id="Output_${uniq}_1" label="Output" typeRef="string"/>
    </dmn:decisionTable>
  </dmn:decision>
  <dmndi:DMNDI>
    <dmndi:DMNDiagram id="DMNDiagram_${uniq}">
      <dmndi:DMNShape id="DMNShape_${uniq}" dmnElementRef="${uniq}">
        <dc:Bounds x="160" y="140" width="180" height="80"/>
      </dmndi:DMNShape>
    </dmndi:DMNDiagram>
  </dmndi:DMNDI>
</dmn:definitions>`;
    return this.importXML(xml);
  }

  async importXML(xml) {
    const { definitions, model, warnings } = await this._reader.read(xml);
    this._definitions = definitions;
    this._model = model;
    if (this._propertiesPanel && typeof this._propertiesPanel.setDefinitions === 'function') {
      this._propertiesPanel.setDefinitions(this._definitions);
    }
    if (this._view === VIEWS.DRD && this._activeEditor) {
      this._activeEditor.render(model);
    } else if (this._view === VIEWS.TABLE && this._activeEditor) {
      if (typeof this._activeEditor.setDefinitions === 'function') {
        this._activeEditor.setDefinitions(this._definitions);
      }
      const pick = this._findDecisionWithTable();
      this._activeEditor.render(pick || {});
    } else if (this._view === VIEWS.EXPRESSION && this._activeEditor) {
      if (typeof this._activeEditor.setDefinitions === 'function') {
        this._activeEditor.setDefinitions(this._definitions);
      }
      const pick = this._findExpressionCandidate();
      this._activeEditor.render(pick || null);
    }
    this._emit('import.done', { definitions, model, warnings });
    this._revalidate();
    return { warnings };
  }

  getModdle() {
    return this._reader.getModdle();
  }

  async exportXML(options) {
    if (!this._definitions) {
      throw new Error('[epx-dmn] no diagram loaded; call importXML first');
    }
    const { xml, warnings } = await this._writer.write(this._definitions, options);
    this._emit('export.done', { xml, warnings });
    return { xml, warnings };
  }

  getDefinitions() {
    return this._definitions;
  }

  getModel() {
    return this._model;
  }

  /**
   * Read-only lookup of a decision (or any DRG element) by id from the
   * underlying moddle definitions. Returns the moddle node so callers can
   * inspect ``decisionLogic`` (e.g. to detect ``epx:MachineLearningDecision``).
   * Used by Atlas chat (sfd-v2) for the ML welcome note (THEMIS_ML §6.3) and
   * the WT-G action dispatcher.
   */
  getDecisionById(id) {
    if (!id || !this._definitions || !Array.isArray(this._definitions.drgElement)) {
      return null;
    }
    return this._definitions.drgElement.find((el) => el && el.id === id) || null;
  }

  /**
   * Lookup a DRG element by id first, then by exact name, then case-insensitive
   * name. Useful for callers that received the user-facing label (e.g. the
   * `decisionRef` of a BPMN BusinessRuleTask) rather than the technical id —
   * `newDecision()` autogenerates an id like `Decision_<random>` while the
   * caller's ref is the human name.
   */
  findDecisionByIdOrName(ref) {
    if (!ref || !this._definitions || !Array.isArray(this._definitions.drgElement)) {
      return null;
    }
    const els = this._definitions.drgElement;
    const byId = els.find((el) => el && el.id === ref);
    if (byId) return byId;
    const byName = els.find((el) => el && el.name === ref);
    if (byName) return byName;
    const lower = String(ref).toLowerCase();
    return els.find((el) => el && typeof el.name === 'string' && el.name.toLowerCase() === lower) || null;
  }

  getActiveEditor() {
    return this._activeEditor;
  }

  /**
   * Returns the active inline boxed-expression / decision-table editor when
   * the user opened a popup inside the DRD (via double-click on a decision).
   * The DRD remains the `view`, so callers that only check `getActiveEditor()`
   * miss this state. Returns `null` when no inline editor is open.
   */
  getInlineEditor() {
    return this._inlineEditor || null;
  }

  /**
   * Returns the current DRD selection as `[{ id, type, businessObject }]`.
   *
   * In TABLE/EXPRESSION views the active "selection" is the focused decision /
   * BKM, which we expose as a single-element array so consumers (e.g. Atlas
   * chat) get a uniform shape across views.
   */
  getSelection() {
    if (this._view === VIEWS.DRD) {
      // Cache populated by the DrdEditor's `selection.changed` bridge below.
      return Array.isArray(this._currentSelection) ? this._currentSelection.slice() : [];
    }
    const ed = this._activeEditor;
    const dec = ed && typeof ed.getDecision === 'function' ? ed.getDecision() : null;
    const bkm = ed && typeof ed.getBkm === 'function' ? ed.getBkm() : null;
    const target = bkm || dec;
    if (!target) return [];
    return [{
      id: target.id,
      type: target.$type || 'dmn:Decision',
      businessObject: target,
    }];
  }

  /**
   * Bridge from DrdEditor's `selection.changed` to EpxDmn's public event.
   * Normalises the LF data shape into `{ id, type, businessObject }`.
   */
  _handleSelectionChanged({ newSelection } = {}) {
    const list = Array.isArray(newSelection) ? newSelection : [];
    const normalised = list.map((el) => {
      const id = el && el.id;
      const type = el && el.type;
      const bo = id ? this._resolveModdleElement({ id, type }) : null;
      return { id, type, businessObject: bo };
    });
    this._currentSelection = normalised;
    this._emit('selection.changed', { elements: normalised });
  }

  /**
   * Public simulator API (PR 1 §4.1). Wraps the lazily-mounted SimulationPanel
   * so external callers (Atlas chat, demos) can drive scenarios without
   * reaching into private internals. Returns `null` when the simulator hasn't
   * been opened yet — open it first via {@link openSimulator}.
   */
  get simulator() {
    const dock = this._simulatorDock;
    const panel = dock && dock._panel ? dock._panel : null;
    return {
      isAvailable: () => !!panel,
      isOpen: () => !!(dock && dock.isOpen && dock.isOpen()),
      open: () => this.openSimulator(),
      close: () => this.closeSimulator(),
      loadScenarios: (scenarios) => {
        const p = (this._simulatorDock && this._simulatorDock._panel) || null;
        if (!p || typeof p.loadScenarios !== 'function') return false;
        return p.loadScenarios(scenarios);
      },
      runAll: () => {
        const p = (this._simulatorDock && this._simulatorDock._panel) || null;
        if (!p || typeof p.runAll !== 'function') return null;
        return p.runAll();
      },
      getResults: () => {
        const p = (this._simulatorDock && this._simulatorDock._panel) || null;
        if (!p || typeof p.getResults !== 'function') return [];
        return p.getResults();
      },
      getScenarios: () => {
        const p = (this._simulatorDock && this._simulatorDock._panel) || null;
        if (!p || typeof p.getScenarios !== 'function') return [];
        return p.getScenarios();
      },
    };
  }

  // ── Native chat-driven mutations (PR 3 §4.4) ─────────────────────────────
  //
  // These thin helpers expose the same operations the editor UI performs
  // when the user clicks the "+ Add row" / "+ Add rule" affordances, so the
  // chat can dispatch them without round-tripping through the LLM. They all
  // mutate moddle nodes directly, then trigger the appropriate change event
  // and re-render so the editor catches up.

  /**
   * Append a default rule to the active decision table. When `decisionId` is
   * omitted the currently focused decision (table view) is used.
   */
  addRule(decisionId = null) {
    const ed = this._activeEditor;
    const targetId = decisionId || (ed && ed.getDecision && ed.getDecision()?.id) || null;
    if (this._view !== VIEWS.TABLE && targetId) {
      this._pendingTableDecisionId = targetId;
      this.setView(VIEWS.TABLE);
    }
    const editor = this._activeEditor;
    if (!editor || typeof editor._addRule !== 'function') return null;
    editor._addRule();
    return { decisionId: targetId };
  }

  /**
   * Append a fresh context entry (variable + literal value) to a Context
   * expression. Returns the created entry so callers can inspect it.
   */
  appendContextEntry(expr) {
    if (!expr || expr.$type !== 'dmn:Context') return null;
    if (!expr.contextEntry) expr.contextEntry = [];
    const moddle = this._reader.getModdle();
    const entry = createContextEntry(moddle, { name: '' });
    entry.$parent = expr;
    expr.contextEntry.push(entry);
    this._notifyExpressionMutation(expr);
    return entry;
  }

  /**
   * Append a fresh literal element to a List expression.
   */
  appendListItem(expr) {
    if (!expr || expr.$type !== 'dmn:List') return null;
    if (!expr.elements) expr.elements = [];
    const moddle = this._reader.getModdle();
    const child = createExpression(moddle, 'dmn:LiteralExpression');
    child.$parent = expr;
    expr.elements.push(child);
    this._notifyExpressionMutation(expr);
    return child;
  }

  /**
   * Append a row to a Relation expression. The row is shaped as a List with
   * one literal cell per existing column (matching the structure produced by
   * the inline "+ Add row" affordance).
   */
  appendRelationRow(expr) {
    if (!expr || expr.$type !== 'dmn:Relation') return null;
    if (!expr.row) expr.row = [];
    const moddle = this._reader.getModdle();
    const row = moddle.create('dmn:List', {
      elements: (expr.column || []).map(() => createExpression(moddle, 'dmn:LiteralExpression')),
    });
    (row.elements || []).forEach((c) => { c.$parent = row; });
    row.$parent = expr;
    expr.row.push(row);
    this._notifyExpressionMutation(expr);
    return row;
  }

  /**
   * Append a column to a Relation expression. Existing rows are extended with
   * a fresh literal cell so the matrix stays rectangular.
   */
  appendRelationColumn(expr) {
    if (!expr || expr.$type !== 'dmn:Relation') return null;
    if (!expr.column) expr.column = [];
    const moddle = this._reader.getModdle();
    const col = createInformationItem(moddle, { name: '' });
    col.$parent = expr;
    expr.column.push(col);
    (expr.row || []).forEach((r) => {
      if (!r.elements) r.elements = [];
      const cell = createExpression(moddle, 'dmn:LiteralExpression');
      cell.$parent = r;
      r.elements.push(cell);
    });
    this._notifyExpressionMutation(expr);
    return col;
  }

  /**
   * Shared aftermath for native expression mutations: re-render the active
   * editor (so the new row/entry shows up), revalidate, and emit the same
   * `expression.changed` event the inline "+" affordance fires.
   */
  _notifyExpressionMutation(expression) {
    this._rerenderActive();
    this._revalidate();
    this._emit('expression.changed', { expression });
    return true;
  }

  clear() {
    this._definitions = null;
    this._model = null;
    if (this._view === VIEWS.DRD && this._activeEditor) {
      this._activeEditor.render(null);
    } else if (this._view === VIEWS.TABLE && this._activeEditor) {
      this._activeEditor.clear();
    } else if (this._view === VIEWS.EXPRESSION && this._activeEditor) {
      this._activeEditor.clear();
    } else {
      this._renderStub();
    }
  }

  getView() {
    return this._view;
  }

  openDecision(decisionId) {
    if (!decisionId) return;
    this._openTableForDecision(decisionId);
  }

  setView(view) {
    if (!Object.values(VIEWS).includes(view)) {
      throw new Error(`[epx-dmn] unknown view "${view}"`);
    }
    this._view = view;
    this._mountView();
    this._emit('view.changed', { view });
    // After switching to TABLE/EXPRESSION the implicit "selection" is the
    // focused decision/BKM. Re-emit so consumers (Atlas chat) refresh their
    // pill / chips without waiting for the next mouse click.
    if (view !== VIEWS.DRD) {
      this._emit('selection.changed', { elements: this.getSelection() });
    }
  }

  fit() {
    if (this._activeEditor && typeof this._activeEditor.fit === 'function') {
      this._activeEditor.fit();
    }
  }

  // ── Simulation visualization bridge ───────────────────────────────────────

  setNodeSimStatus(nodeId, status) {
    if (this._view !== VIEWS.DRD) return;
    const ed = this._activeEditor;
    if (ed && typeof ed.setNodeSimStatus === 'function') {
      ed.setNodeSimStatus(nodeId, status);
    }
  }

  clearAllSimStatus() {
    if (this._view !== VIEWS.DRD) return;
    const ed = this._activeEditor;
    if (ed && typeof ed.clearAllSimStatus === 'function') {
      ed.clearAllSimStatus();
    }
  }

  // ── Simulator dock API ────────────────────────────────────────────────────

  toggleSimulator() { if (this._simulatorDock) this._simulatorDock.toggle(); }

  openSimulator() { if (this._simulatorDock) this._simulatorDock.open(); }

  closeSimulator() { if (this._simulatorDock) this._simulatorDock.close(); }

  isSimulatorOpen() { return !!(this._simulatorDock && this._simulatorDock.isOpen()); }

  setSimulatorStorageKey(key) {
    if (this._simulatorDock) this._simulatorDock.setStorageKey(key);
  }

  _handleSimulatorReveal({ decisionId, ruleId, ruleIdx, colIdx, kind, conflictRuleIdxs } = {}) {
    if (decisionId) this._revealDecisionRule({ decisionId, ruleId, ruleIdx, colIdx, kind, conflictRuleIdxs });
    this._emit('simulator:reveal', { decisionId, ruleId, ruleIdx, colIdx, kind, conflictRuleIdxs });
  }

  _revealDecisionRule({ decisionId, ruleId, ruleIdx, colIdx, kind, conflictRuleIdxs }) {
    if (!decisionId) return;
    try {
      if (this._view !== VIEWS.TABLE) {
        this._pendingTableDecisionId = decisionId;
        this.setView(VIEWS.TABLE);
      } else {
        const editor = this.getActiveEditor();
        const cur = editor && editor.getDecision && editor.getDecision();
        if (!cur || cur.id !== decisionId) {
          this._pendingTableDecisionId = decisionId;
          this.setView(VIEWS.TABLE);
        }
      }
    } catch (err) {
      console.warn('[epx-dmn] simulator reveal: setView failed', err);
      return;
    }
    setTimeout(() => {
      const editor = this.getActiveEditor();
      if (!editor) return;
      if (Array.isArray(conflictRuleIdxs) && conflictRuleIdxs.length
          && typeof editor.setSimulationHighlights === 'function') {
        editor.setSimulationHighlights({ conflict: conflictRuleIdxs });
        if (typeof editor.revealRule === 'function') editor.revealRule(conflictRuleIdxs[0]);
        return;
      }
      if (typeof editor.revealRule === 'function') {
        editor.revealRule(ruleId != null ? ruleId : ruleIdx, kind, colIdx);
      }
    }, 60);
  }

  // ── DRD → moddle bridge ───────────────────────────────────────────────────

  _ensureDefinitions() {
    if (this._definitions) return this._definitions;
    const moddle = this._reader.getModdle();
    const uniq = _uid();
    this._definitions = moddle.create('dmn:Definitions', {
      id: 'Definitions_' + uniq,
      name: 'Definitions_' + uniq,
      namespace: `https://epx.com/dmn/${uniq}`,
      drgElement: [],
      artifact: [],
    });
    return this._definitions;
  }

  _ensureDmnDiagram() {
    const defs = this._ensureDefinitions();
    const moddle = this._reader.getModdle();
    if (!defs.dmnDI) {
      defs.dmnDI = moddle.create('dmndi:DMNDI', { diagrams: [] });
      defs.dmnDI.$parent = defs;
    }
    if (!defs.dmnDI.diagrams || !defs.dmnDI.diagrams.length) {
      const diagram = moddle.create('dmndi:DMNDiagram', {
        id: 'DMNDiagram_' + _uid(),
        diagramElements: [],
      });
      diagram.$parent = defs.dmnDI;
      defs.dmnDI.diagrams = [diagram];
    }
    return defs.dmnDI.diagrams[0];
  }

  _rebuildModel() {
    if (!this._definitions) return;
    this._model = buildModel(this._definitions);
  }

  _findDrg(id) {
    const defs = this._definitions;
    if (!defs || !defs.drgElement) return null;
    return defs.drgElement.find((e) => e.id === id) || null;
  }

  _bridgeOnElementAdded({ element }) {
    if (!element || !element.id || !element.type) return;
    const defs = this._ensureDefinitions();
    if (this._findDrg(element.id)) return; // already present (came from render)

    const moddle = this._reader.getModdle();
    const type = element.type;
    const name = element.properties?.name || element.text?.value || '';
    const sz = SHAPE_DEFAULTS[type] || { width: 140, height: 60 };
    const width = element.properties?.width || sz.width;
    const height = element.properties?.height || sz.height;

    let drg;
    if (type === 'dmn:Decision') {
      const preset = element.properties?.preset;
      let logic;
      if (preset === 'ml') {
        logic = createExpression(moddle, 'epx:MachineLearningDecision');
      } else {
        const inputExpr = moddle.create('dmn:LiteralExpression', { id: 'InputExpr_' + _uid(), text: '' });
        const input = moddle.create('dmn:InputClause', { id: 'Input_' + _uid(), inputExpression: inputExpr, label: 'Input' });
        inputExpr.$parent = input;
        const output = moddle.create('dmn:OutputClause', { id: 'Output_' + _uid(), label: 'Output', typeRef: 'string' });
        logic = moddle.create('dmn:DecisionTable', {
          id: 'DecisionTable_' + _uid(),
          hitPolicy: 'UNIQUE',
          input: [input],
          output: [output],
          rule: [],
        });
        input.$parent = logic;
        output.$parent = logic;
      }
      drg = moddle.create('dmn:Decision', { id: element.id, name, decisionLogic: logic });
      logic.$parent = drg;
    } else if (type === 'dmn:InputData') {
      const catalogTypeRef = element.properties?.catalogTypeRef;
      if (catalogTypeRef) {
        const variable = moddle.create('dmn:InformationItem', {
          id: element.id + '_var',
          name: sanitizeFeelIdent(name) || name,
          typeRef: catalogTypeRef,
        });
        drg = moddle.create('dmn:InputData', { id: element.id, name, variable });
        variable.$parent = drg;
      } else {
        drg = moddle.create('dmn:InputData', { id: element.id, name });
      }
    } else if (type === 'dmn:BusinessKnowledgeModel') {
      const bkmBody = moddle.create('dmn:LiteralExpression', { id: 'LitExpr_' + _uid(), text: '' });
      const funcDef = moddle.create('dmn:FunctionDefinition', {
        id: 'FunctionDef_' + _uid(),
        kind: 'FEEL',
        formalParameter: [],
        body: bkmBody,
      });
      bkmBody.$parent = funcDef;
      drg = moddle.create('dmn:BusinessKnowledgeModel', { id: element.id, name, encapsulatedLogic: funcDef });
      funcDef.$parent = drg;
    } else if (type === 'dmn:KnowledgeSource') {
      drg = moddle.create('dmn:KnowledgeSource', { id: element.id, name });
    } else {
      return;
    }

    drg.$parent = defs;
    if (!defs.drgElement) defs.drgElement = [];
    defs.drgElement.push(drg);

    const diagram = this._ensureDmnDiagram();
    const bounds = moddle.create('dc:Bounds', {
      x: (element.x || 0) - width / 2,
      y: (element.y || 0) - height / 2,
      width,
      height,
    });
    const shape = moddle.create('dmndi:DMNShape', {
      id: 'DMNShape_' + _uid(),
      dmnElementRef: drg,
      bounds,
    });
    bounds.$parent = shape;
    shape.$parent = diagram;
    if (!diagram.diagramElements) diagram.diagramElements = [];
    diagram.diagramElements.push(shape);

    this._rebuildModel();
    this._revalidate();
    this._emit('element.changed', { element, change: 'added' });
  }

  _bridgeOnElementRemoved({ element }) {
    const defs = this._definitions;
    if (!defs || !element || !element.id) return;

    const removed = (defs.drgElement || []).find((e) => e.id === element.id);
    const removedName = removed && removed.name;
    defs.drgElement = (defs.drgElement || []).filter((e) => e.id !== element.id);

    const refProps = ['requiredDecision', 'requiredInput', 'requiredKnowledge', 'requiredAuthority'];
    const reqProps = ['informationRequirement', 'knowledgeRequirement', 'authorityRequirement'];
    (defs.drgElement || []).forEach((e) => {
      reqProps.forEach((prop) => {
        if (!e[prop]) return;
        e[prop] = e[prop].filter((req) =>
          !refProps.some((r) => {
            const ref = req[r];
            if (!ref) return false;
            return ref.id === element.id || ref.href === '#' + element.id;
          })
        );
      });
    });

    // Invocations reference the called function by name/id text — clear any
    // that pointed to the removed element so we don't leave orphan references.
    if (removedName || element.id) {
      (defs.drgElement || []).forEach((drg) => {
        const logic = drg.decisionLogic;
        if (!logic || logic.$type !== 'dmn:Invocation') return;
        const called = logic.calledFunction;
        if (!called) return;
        if (called.text === removedName || called.text === element.id) {
          called.text = '';
        }
      });
    }

    const diagram = defs.dmnDI && defs.dmnDI.diagrams && defs.dmnDI.diagrams[0];
    if (diagram && diagram.diagramElements) {
      diagram.diagramElements = diagram.diagramElements.filter((de) => {
        const ref = de.dmnElementRef;
        if (!ref) return true;
        const refId = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        return refId !== element.id;
      });
    }

    this._rebuildModel();
    this._revalidate();
    this._emit('element.changed', { element, change: 'removed' });
  }

  _bridgeOnElementMoved({ element }) {
    const defs = this._definitions;
    if (!defs || !element || !element.id) return;
    const diagram = defs.dmnDI && defs.dmnDI.diagrams && defs.dmnDI.diagrams[0];
    if (!diagram) return;
    const shape = (diagram.diagramElements || []).find((de) => {
      if (de.$type !== 'dmndi:DMNShape') return false;
      const ref = de.dmnElementRef;
      if (!ref) return false;
      const refId = ref.id || (ref.href && ref.href.replace(/^#/, ''));
      return refId === element.id;
    });
    if (!shape || !shape.bounds) return;
    const w = shape.bounds.width || 140;
    const h = shape.bounds.height || 60;
    shape.bounds.x = (element.x || 0) - w / 2;
    shape.bounds.y = (element.y || 0) - h / 2;
    this._rebuildModel();
    this._emit('element.changed', { element, change: 'moved' });
  }

  _bridgeOnElementRenamed({ element }) {
    const drg = this._findDrg(element && element.id);
    if (!drg) return;
    const oldName = drg.name;
    const newName = (element.text && element.text.value) || element.properties?.name || '';
    drg.name = newName || undefined;
    // Invocations reference the called function by name, so renaming a
    // BKM/Decision must rewrite any matching calledFunction text.
    if (oldName && oldName !== newName) {
      this._retargetInvocations(oldName, newName || undefined);
    }
    this._rebuildModel();
    this._emit('element.changed', { element, change: 'renamed' });
  }

  _retargetInvocations(oldName, newName) {
    const defs = this._definitions;
    if (!defs || !defs.drgElement) return;
    defs.drgElement.forEach((drg) => {
      const logic = drg.decisionLogic;
      if (!logic || logic.$type !== 'dmn:Invocation') return;
      const called = logic.calledFunction;
      if (!called) return;
      if (called.text === oldName) called.text = newName || '';
    });
  }

  /**
   * Re-sync every connected Decision's Invocation bindings with the current
   * formalParameter list of the given BKM. Keeps user-written formulas for
   * parameters whose name still matches; adds bindings for new params; removes
   * bindings for params that no longer exist. Returns the number of Decisions
   * whose bindings were updated.
   */
  syncBkmBindings(bkmId) {
    const defs = this._definitions;
    if (!defs || !defs.drgElement || !bkmId) return 0;
    const bkm = defs.drgElement.find(
      (e) => e.id === bkmId && e.$type === 'dmn:BusinessKnowledgeModel'
    );
    if (!bkm) return 0;
    const moddle = this._reader.getModdle();
    const params = (bkm.encapsulatedLogic && bkm.encapsulatedLogic.formalParameter) || [];
    const bkmName = bkm.name;

    let updated = 0;
    defs.drgElement.forEach((drg) => {
      if (drg.$type !== 'dmn:Decision') return;
      const logic = drg.decisionLogic;
      if (!logic || logic.$type !== 'dmn:Invocation') return;
      const called = logic.calledFunction;
      // Match by KnowledgeRequirement href (authoritative) OR by calledFunction.text.
      const linkedByKr = (drg.knowledgeRequirement || []).some((kr) => {
        const ref = kr.requiredKnowledge;
        const rid = ref && (ref.id || (ref.href && ref.href.replace(/^#/, '')));
        return rid === bkmId;
      });
      const linkedByText = called && (called.text === bkmName || called.text === bkmId);
      if (!linkedByKr && !linkedByText) return;

      const previous = logic.binding || [];
      const byName = new Map();
      previous.forEach((b) => {
        const n = b.parameter && b.parameter.name;
        if (n) byName.set(n, b);
      });

      const next = params.map((param) => {
        const pname = param.name || '';
        const existing = pname && byName.get(pname);
        if (existing) {
          // Preserve user-authored formula; sync typeRef in case it changed.
          if (existing.parameter) existing.parameter.typeRef = param.typeRef;
          byName.delete(pname);
          return existing;
        }
        const paramVar = moddle.create('dmn:InformationItem', {
          id: 'Var_' + _uid(),
          name: pname,
          typeRef: param.typeRef,
        });
        const bindingFormula = moddle.create('dmn:LiteralExpression', {
          id: 'LitExpr_' + _uid(),
          text: pname,
        });
        const binding = moddle.create('dmn:Binding', {
          parameter: paramVar,
          bindingFormula,
        });
        paramVar.$parent = binding;
        bindingFormula.$parent = binding;
        binding.$parent = logic;
        return binding;
      });

      // Detect actual change (length or identity) before counting as updated.
      const changed = next.length !== previous.length
        || next.some((b, i) => b !== previous[i])
        || byName.size > 0;
      logic.binding = next;
      if (called && bkmName && called.text !== bkmName && called.text !== bkmId) {
        // Nothing — we keep the existing text (user may have customized it).
      }
      if (changed) updated += 1;
    });

    if (updated > 0) {
      this._rebuildModel();
      this._revalidate();
      this._rerenderActive();
      this._emit('element.changed', { element: bkm, change: 'bkmBindingsSynced' });
    }
    return updated;
  }

  // Walk the KnowledgeRequirements of `decision`, union their BKMs'
  // formalParameters, and for every param name that isn't already reachable
  // (as an informationRequirement from an InputData/Decision with the same
  // name) create an InputData + InformationRequirement and wire the DMNDI.
  createMissingBkmInputs(decisionId) {
    const decision = this._findDrg(decisionId);
    if (!decision || decision.$type !== 'dmn:Decision') return 0;
    const defs = this._ensureDefinitions();
    const moddle = this._reader.getModdle();

    const knowledgeReqs = decision.knowledgeRequirement || [];
    const params = [];
    const seen = new Set();
    knowledgeReqs.forEach((kr) => {
      const href = kr.requiredKnowledge && kr.requiredKnowledge.href;
      const bkmId = (href || '').split('#').pop();
      const bkm = (defs.drgElement || []).find((e) => e.id === bkmId && e.$type === 'dmn:BusinessKnowledgeModel');
      if (!bkm) return;
      const fps = (bkm.encapsulatedLogic && bkm.encapsulatedLogic.formalParameter) || [];
      fps.forEach((p) => {
        const name = (p.name || '').trim();
        if (!name || seen.has(name)) return;
        seen.add(name);
        params.push({ name, typeRef: p.typeRef || 'string' });
      });
    });
    if (!params.length) return 0;

    const alreadyConnected = new Set();
    (decision.informationRequirement || []).forEach((req) => {
      const ref = req.requiredInput || req.requiredDecision;
      const id = ref && (ref.href || '').split('#').pop();
      if (!id) return;
      const tgt = (defs.drgElement || []).find((e) => e.id === id);
      if (tgt && tgt.name) alreadyConnected.add(tgt.name.trim());
    });

    let created = 0;
    params.forEach(({ name, typeRef }) => {
      if (alreadyConnected.has(name)) return;

      let inputData = (defs.drgElement || []).find(
        (e) => e.$type === 'dmn:InputData' && (e.name || '').trim() === name,
      );
      if (!inputData) {
        const newId = 'InputData_' + _uid();
        const variable = moddle.create('dmn:InformationItem', {
          id: newId + '_var',
          name: sanitizeFeelIdent(name) || name,
          typeRef,
        });
        inputData = moddle.create('dmn:InputData', { id: newId, name, variable });
        variable.$parent = inputData;
        inputData.$parent = defs;
        if (!defs.drgElement) defs.drgElement = [];
        defs.drgElement.push(inputData);

        const diagram = this._ensureDmnDiagram();
        const decShape = (diagram.diagramElements || []).find((de) => {
          if (de.$type !== 'dmndi:DMNShape') return false;
          const ref = de.dmnElementRef;
          if (!ref) return false;
          const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
          return rid === decision.id;
        });
        const dx = decShape && decShape.bounds ? decShape.bounds.x : 200;
        const dy = decShape && decShape.bounds ? decShape.bounds.y : 200;
        const sz = SHAPE_DEFAULTS['dmn:InputData'];
        const bounds = moddle.create('dc:Bounds', {
          x: dx - sz.width - 40 - created * (sz.width + 20),
          y: dy + 160,
          width: sz.width,
          height: sz.height,
        });
        const shape = moddle.create('dmndi:DMNShape', {
          id: 'DMNShape_' + _uid(),
          dmnElementRef: inputData,
          bounds,
        });
        bounds.$parent = shape;
        shape.$parent = diagram;
        if (!diagram.diagramElements) diagram.diagramElements = [];
        diagram.diagramElements.push(shape);
      }

      const refElement = moddle.create('dmn:DMNElementReference', { href: '#' + inputData.id });
      const req = moddle.create('dmn:InformationRequirement', {
        id: 'Requirement_' + _uid(),
        requiredInput: refElement,
      });
      refElement.$parent = req;
      req.$parent = decision;
      if (!decision.informationRequirement) decision.informationRequirement = [];
      decision.informationRequirement.push(req);

      const diagram = this._ensureDmnDiagram();
      const findShape = (id) => (diagram.diagramElements || []).find((de) => {
        if (de.$type !== 'dmndi:DMNShape') return false;
        const ref = de.dmnElementRef;
        if (!ref) return false;
        const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        return rid === id;
      });
      const inputShape = findShape(inputData.id);
      const decisionShape = findShape(decision.id);
      const ib = (inputShape && inputShape.bounds) || null;
      const db = (decisionShape && decisionShape.bounds) || null;
      const startPoint = ib
        ? { x: (ib.x || 0) + (ib.width || 0) / 2, y: (ib.y || 0) }
        : { x: 0, y: 0 };
      const endPoint = db
        ? { x: (db.x || 0) + (db.width || 0) / 2, y: (db.y || 0) + (db.height || 0) }
        : { x: startPoint.x, y: startPoint.y + 200 };
      const wp = [
        moddle.create('dc:Point', { x: startPoint.x, y: startPoint.y }),
        moddle.create('dc:Point', { x: endPoint.x, y: endPoint.y }),
      ];
      const dmnEdge = moddle.create('dmndi:DMNEdge', {
        id: 'DMNEdge_' + _uid(),
        dmnElementRef: req,
        waypoint: wp,
      });
      wp.forEach((p) => (p.$parent = dmnEdge));
      dmnEdge.$parent = diagram;
      if (!diagram.diagramElements) diagram.diagramElements = [];
      diagram.diagramElements.push(dmnEdge);

      created += 1;
      alreadyConnected.add(name);
    });

    if (created > 0) {
      this._rebuildModel();
      this._revalidate();
      this._rerenderActive();
      this._emit('element.changed', { element: decision, change: 'bkmInputsCreated' });
    }
    return created;
  }

  // Public API — add one or more InputData nodes to a Decision and wire the
  // matching DMNShape + DMNEdge. Used by the Themis assistant when the user
  // accepts a "Sugerir inputs faltantes" suggestion: each entry becomes a real
  // InputData (or reuses the existing one with the same name) plus an
  // InformationRequirement so it shows up in the DRD wired to the decision.
  // Accepts either a single `{name, typeRef}` or an array. Returns the number
  // of entries actually created.
  addInputDataForDecision(decisionId, entries) {
    const decision = this._findDrg(decisionId);
    if (!decision || decision.$type !== 'dmn:Decision') return 0;
    const list = Array.isArray(entries) ? entries : (entries ? [entries] : []);
    if (!list.length) return 0;
    const defs = this._ensureDefinitions();
    const moddle = this._reader.getModdle();

    const alreadyConnected = new Set();
    (decision.informationRequirement || []).forEach((req) => {
      const ref = req.requiredInput || req.requiredDecision;
      const id = ref && (ref.href || '').split('#').pop();
      if (!id) return;
      const tgt = (defs.drgElement || []).find((e) => e.id === id);
      if (tgt && tgt.name) alreadyConnected.add(tgt.name.trim());
    });

    let created = 0;
    list.forEach((entry) => {
      const name = ((entry && entry.name) || '').trim();
      if (!name || alreadyConnected.has(name)) return;
      const typeRef = (entry && entry.typeRef) || 'string';

      let inputData = (defs.drgElement || []).find(
        (e) => e.$type === 'dmn:InputData' && (e.name || '').trim() === name,
      );
      if (!inputData) {
        const newId = 'InputData_' + _uid();
        const variable = moddle.create('dmn:InformationItem', {
          id: newId + '_var',
          name: sanitizeFeelIdent(name) || name,
          typeRef,
        });
        inputData = moddle.create('dmn:InputData', { id: newId, name, variable });
        variable.$parent = inputData;
        inputData.$parent = defs;
        if (!defs.drgElement) defs.drgElement = [];
        defs.drgElement.push(inputData);

        const diagram = this._ensureDmnDiagram();
        const decShape = (diagram.diagramElements || []).find((de) => {
          if (de.$type !== 'dmndi:DMNShape') return false;
          const ref = de.dmnElementRef;
          if (!ref) return false;
          const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
          return rid === decision.id;
        });
        const dx = decShape && decShape.bounds ? decShape.bounds.x : 200;
        const dy = decShape && decShape.bounds ? decShape.bounds.y : 200;
        const sz = SHAPE_DEFAULTS['dmn:InputData'];
        const bounds = moddle.create('dc:Bounds', {
          x: dx - sz.width - 40 - created * (sz.width + 20),
          y: dy + 160,
          width: sz.width,
          height: sz.height,
        });
        const shape = moddle.create('dmndi:DMNShape', {
          id: 'DMNShape_' + _uid(),
          dmnElementRef: inputData,
          bounds,
        });
        bounds.$parent = shape;
        shape.$parent = diagram;
        if (!diagram.diagramElements) diagram.diagramElements = [];
        diagram.diagramElements.push(shape);
      }

      const refElement = moddle.create('dmn:DMNElementReference', { href: '#' + inputData.id });
      const req = moddle.create('dmn:InformationRequirement', {
        id: 'Requirement_' + _uid(),
        requiredInput: refElement,
      });
      refElement.$parent = req;
      req.$parent = decision;
      if (!decision.informationRequirement) decision.informationRequirement = [];
      decision.informationRequirement.push(req);

      const diagram = this._ensureDmnDiagram();
      const findShape = (id) => (diagram.diagramElements || []).find((de) => {
        if (de.$type !== 'dmndi:DMNShape') return false;
        const ref = de.dmnElementRef;
        if (!ref) return false;
        const rid = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        return rid === id;
      });
      const inputShape = findShape(inputData.id);
      const decisionShape = findShape(decision.id);
      const ib = (inputShape && inputShape.bounds) || null;
      const db = (decisionShape && decisionShape.bounds) || null;
      const startPoint = ib
        ? { x: (ib.x || 0) + (ib.width || 0) / 2, y: (ib.y || 0) }
        : { x: 0, y: 0 };
      const endPoint = db
        ? { x: (db.x || 0) + (db.width || 0) / 2, y: (db.y || 0) + (db.height || 0) }
        : { x: startPoint.x, y: startPoint.y + 200 };
      const wp = [
        moddle.create('dc:Point', { x: startPoint.x, y: startPoint.y }),
        moddle.create('dc:Point', { x: endPoint.x, y: endPoint.y }),
      ];
      const dmnEdge = moddle.create('dmndi:DMNEdge', {
        id: 'DMNEdge_' + _uid(),
        dmnElementRef: req,
        waypoint: wp,
      });
      wp.forEach((p) => (p.$parent = dmnEdge));
      dmnEdge.$parent = diagram;
      if (!diagram.diagramElements) diagram.diagramElements = [];
      diagram.diagramElements.push(dmnEdge);

      created += 1;
      alreadyConnected.add(name);
    });

    if (created > 0) {
      this._rebuildModel();
      this._revalidate();
      this._rerenderActive();
      this._emit('element.changed', { element: decision, change: 'inputsAdded' });
    }
    return created;
  }

  _bridgeOnEdgeAdded({ edge }) {
    if (!edge || !edge.sourceNodeId || !edge.targetNodeId) return;
    const defs = this._ensureDefinitions();
    const moddle = this._reader.getModdle();
    const source = this._findDrg(edge.sourceNodeId);
    const target = this._findDrg(edge.targetNodeId);
    if (!source || !target) return;

    const srcType = source.$type;
    const edgeType = edge.type || 'dmn:InformationRequirement';

    let reqType, containerProp, refProp;
    if (edgeType === 'dmn:KnowledgeRequirement' || srcType === 'dmn:BusinessKnowledgeModel') {
      reqType = 'dmn:KnowledgeRequirement';
      containerProp = 'knowledgeRequirement';
      refProp = 'requiredKnowledge';
    } else if (edgeType === 'dmn:AuthorityRequirement' || srcType === 'dmn:KnowledgeSource') {
      reqType = 'dmn:AuthorityRequirement';
      containerProp = 'authorityRequirement';
      refProp = srcType === 'dmn:KnowledgeSource' ? 'requiredAuthority'
              : srcType === 'dmn:InputData'       ? 'requiredInput'
              : 'requiredDecision';
    } else {
      reqType = 'dmn:InformationRequirement';
      containerProp = 'informationRequirement';
      refProp = srcType === 'dmn:InputData' ? 'requiredInput' : 'requiredDecision';
    }

    const refElement = moddle.create('dmn:DMNElementReference', { href: '#' + source.id });
    const reqProps = { id: edge.id || ('Requirement_' + _uid()) };
    reqProps[refProp] = refElement;
    const req = moddle.create(reqType, reqProps);
    refElement.$parent = req;
    req.$parent = target;
    if (!target[containerProp]) target[containerProp] = [];
    target[containerProp].push(req);

    const diagram = this._ensureDmnDiagram();
    const pts = (edge.pointsList && edge.pointsList.length
      ? edge.pointsList
      : [edge.startPoint || { x: 0, y: 0 }, edge.endPoint || { x: 0, y: 0 }]
    ).map((p) => moddle.create('dc:Point', { x: p.x || 0, y: p.y || 0 }));
    const dmnEdge = moddle.create('dmndi:DMNEdge', {
      id: 'DMNEdge_' + _uid(),
      dmnElementRef: req,
      waypoint: pts,
    });
    pts.forEach((p) => (p.$parent = dmnEdge));
    dmnEdge.$parent = diagram;
    if (!diagram.diagramElements) diagram.diagramElements = [];
    diagram.diagramElements.push(dmnEdge);

    this._rebuildModel();
    this._revalidate();
    this._emit('element.changed', { edge, change: 'edgeAdded' });
  }

  _bridgeOnEdgeRemoved({ edge }) {
    const defs = this._definitions;
    if (!defs || !edge || !edge.id) return;
    const reqProps = ['informationRequirement', 'knowledgeRequirement', 'authorityRequirement'];
    (defs.drgElement || []).forEach((drg) => {
      reqProps.forEach((prop) => {
        if (!drg[prop]) return;
        drg[prop] = drg[prop].filter((r) => r.id !== edge.id);
      });
    });
    const diagram = defs.dmnDI && defs.dmnDI.diagrams && defs.dmnDI.diagrams[0];
    if (diagram && diagram.diagramElements) {
      diagram.diagramElements = diagram.diagramElements.filter((de) => {
        if (de.$type !== 'dmndi:DMNEdge') return true;
        const ref = de.dmnElementRef;
        if (!ref) return true;
        const refId = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        return refId !== edge.id;
      });
    }
    this._rebuildModel();
    this._revalidate();
    this._emit('element.changed', { edge, change: 'edgeRemoved' });
  }

  _bridgeOnLayoutApplied({ nodes = [], edges = [] } = {}) {
    const defs = this._definitions;
    if (!defs) return;
    const diagram = defs.dmnDI && defs.dmnDI.diagrams && defs.dmnDI.diagrams[0];
    if (!diagram || !diagram.diagramElements) return;
    const moddle = this._reader.getModdle();

    const shapeByRef = new Map();
    const edgeById = new Map();
    diagram.diagramElements.forEach((de) => {
      if (de.$type === 'dmndi:DMNShape') {
        const ref = de.dmnElementRef;
        if (!ref) return;
        const refId = ref.id || (ref.href && ref.href.replace(/^#/, ''));
        if (refId) shapeByRef.set(refId, de);
      } else if (de.$type === 'dmndi:DMNEdge') {
        // LogicFlow edge IDs come from the DMNEdge's own id (set in model.js line 93),
        // not from its dmnElementRef (the requirement). Key by de.id.
        if (de.id) edgeById.set(de.id, de);
      }
    });

    nodes.forEach(({ id, x, y, width, height }) => {
      const shape = shapeByRef.get(id);
      if (!shape || !shape.bounds) return;
      const w = typeof width === 'number' ? width : shape.bounds.width;
      const h = typeof height === 'number' ? height : shape.bounds.height;
      shape.bounds.x = x - w / 2;
      shape.bounds.y = y - h / 2;
      shape.bounds.width = w;
      shape.bounds.height = h;
    });

    edges.forEach(({ id, pointsList }) => {
      const dmnEdge = edgeById.get(id);
      if (!dmnEdge || !pointsList || !pointsList.length) return;
      const pts = pointsList.map((p) => moddle.create('dc:Point', { x: p.x, y: p.y }));
      pts.forEach((p) => (p.$parent = dmnEdge));
      dmnEdge.waypoint = pts;
    });

    this._rebuildModel();
    this._emit('element.changed', { change: 'layout' });
  }

  autoLayout(options) {
    if (this._view !== VIEWS.DRD) return Promise.resolve();
    const editor = this._activeEditor;
    if (!editor || typeof editor.autoLayout !== 'function') return Promise.resolve();
    return editor.autoLayout(options);
  }

  // ── Events ────────────────────────────────────────────────────────────────

  on(event, handler) {
    if (!this._eventHandlers[event]) this._eventHandlers[event] = [];
    this._eventHandlers[event].push(handler);
  }

  off(event, handler) {
    const list = this._eventHandlers[event];
    if (!list) return;
    this._eventHandlers[event] = list.filter((h) => h !== handler);
  }

  _emit(event, payload) {
    const list = this._eventHandlers[event];
    if (!list) return;
    list.forEach((h) => {
      try { h(payload); } catch (err) { console.error(`[epx-dmn] handler error for "${event}":`, err); }
    });
  }

  // ── Theme & grid ──────────────────────────────────────────────────────────

  /**
   * Mirror the active theme onto every DOM node sfd-v2 (or any host) handed
   * us — both the editor container and the properties container live in
   * separate subtrees, so we have to set the CSS variables on each.
   */
  _applyThemeToHostElements() {
    if (!this._currentTheme) return;
    if (this._containerEl)            applyThemeCssVars(this._containerEl,           this._currentTheme);
    if (this._propertiesContainerEl)  applyThemeCssVars(this._propertiesContainerEl, this._currentTheme);
    // Apply canvas surface color directly so the area outside the active
    // editor (e.g. simulator dock) still picks up the theme's background.
    const bg = this._currentTheme.canvas && this._currentTheme.canvas.background;
    if (bg && this._containerEl) this._containerEl.style.background = bg;
  }

  /** Resolve a theme input (name or object) into a normalised DMN theme. */
  _resolveThemeInput(input) {
    if (!input) return resolveTheme(null);
    if (typeof input === 'string') {
      const found = this._themeRegistry.get(input);
      return resolveTheme(found || null);
    }
    return resolveTheme(input);
  }

  /**
   * Register a custom theme so it can be selected later by name. Accepts
   * either a DMN-native palette or a BPMN/epx-flow theme object (in which
   * case `theme.dmn` provides DMN-specific colors and panel/canvas/grid/text
   * are mirrored to CSS variables).
   */
  registerTheme(name, theme) {
    if (!name || !theme) return;
    this._themeRegistry.set(name, theme);
  }

  getRegisteredThemes() {
    return Array.from(this._themeRegistry.keys());
  }

  /** Apply a theme by name (must be registered) or by object. */
  setTheme(theme) {
    const resolved = this._resolveThemeInput(theme);
    this._currentTheme = resolved;
    this._currentThemeName = typeof theme === 'string' ? theme : null;
    this._applyThemeToHostElements();
    if (this._activeEditor && typeof this._activeEditor.setTheme === 'function') {
      try { this._activeEditor.setTheme(resolved); } catch (err) { console.warn('[epx-dmn] setTheme on active editor failed:', err); }
    }
    if (this._propertiesPanel && typeof this._propertiesPanel.refresh === 'function') {
      try { this._propertiesPanel.refresh(); } catch { /* noop */ }
    }
    this._emit('theme.changed', { theme: this._currentThemeName, value: resolved });
  }

  /** Update the DRD canvas grid (visible/type/size/color). */
  setGrid(opts = {}) {
    this._gridOptions = { ...(this._gridOptions || {}), ...opts };
    if (this._view === VIEWS.DRD && this._activeEditor && typeof this._activeEditor.setGrid === 'function') {
      const live = this._activeEditor.setGrid(this._gridOptions);
      if (!live) {
        // Editor signalled it needs a rebuild to honour the new grid.
        this._mountView();
      }
    }
    this._emit('grid.changed', { grid: { ...this._gridOptions } });
  }

  setGridVisible(visible) {
    this.setGrid({ visible: !!visible });
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  /**
   * Recomputes layout for the active editor (DRD canvas, decision-table, or
   * boxed-expression view), the simulator dock splitter, and the inline
   * overlay. Call after the host container changes size — e.g. when the DMN
   * popup overlay is resized or its mode (popup/dock/full) toggles.
   */
  invalidateLayout() {
    if (this._activeEditor && typeof this._activeEditor.resize === 'function') {
      try { this._activeEditor.resize(); } catch (err) { console.warn('[epx-dmn] invalidateLayout: editor resize failed', err); }
    }
    if (this._simulatorDock && typeof this._simulatorDock.resize === 'function') {
      try { this._simulatorDock.resize(); } catch (err) { console.warn('[epx-dmn] invalidateLayout: simulator resize failed', err); }
    }
    if (this._inlineEditor && typeof this._inlineEditor.resize === 'function') {
      try { this._inlineEditor.resize(); } catch { /* noop */ }
    }
    this._emit('layout.invalidated', {});
  }

  // ── i18n ──────────────────────────────────────────────────────────────────

  t(key) {
    return (this._i18n && this._i18n[key]) || key;
  }

  getLocale() {
    return this._locale;
  }

  getRegisteredLocales() {
    return Object.keys(I18N);
  }

  setLocale(locale) {
    if (!I18N[locale]) I18N[locale] = { ...I18N.en };
    this._locale = locale;
    this._i18n = I18N[locale];
    // Re-mount active editor and refresh the properties panel so every label
    // (tabs, toolbars, empty states, headers) picks up the new translations.
    this._mountView();
    if (this._propertiesPanel && typeof this._propertiesPanel.refresh === 'function') {
      this._propertiesPanel.refresh();
    }
    this._emit('locale.changed', { locale });
  }

  addTranslations(locale, translations) {
    if (!I18N[locale]) I18N[locale] = {};
    Object.assign(I18N[locale], translations);
    if (this._locale === locale) this._i18n = I18N[locale];
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  destroy() {
    this._closeInlineEditor();
    this._disposeActiveEditor();
    if (this._simulatorDock) { this._simulatorDock.destroy(); this._simulatorDock = null; }
    if (this._propertiesPanel) { this._propertiesPanel.destroy(); this._propertiesPanel = null; }
    if (this._containerEl) {
      this._containerEl.innerHTML = '';
      this._containerEl.classList.remove('epx-dmn', 'epx-sim-open');
    }
    this._editorAreaEl = null;
    this._eventHandlers = {};
  }
}
